import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ViewChild,
  computed,
  model,
  signal,
  inject,
} from '@angular/core';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { UniversalSearchService } from '../../../../universal-search/services/universal-search.service';
import { CustomFiltersService } from '../../../../services/custom-filters.service';
import { SessionStorageService } from '../../../../services/session-storage.service';
import { Subject } from 'rxjs';
import {
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
  MatAutocompleteModule,
} from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { DtccService } from '../../../../annuity-lookup/dtcc.service';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-npn-filter',
  templateUrl: './npn-filter.component.html',
  styleUrls: ['./npn-filter.component.scss'],
  imports: [
    ReactiveFormsModule,
    FormsModule,
    CommonModule,
    MatAutocompleteModule,
    MatInputModule,
    MatChipsModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
})
export class NpnFilterComponent implements OnInit, OnDestroy {
  cstmFltrSrvc = inject(CustomFiltersService);
  private dtccSvc = inject(DtccService);
  private ss = inject(SessionStorageService);
  usServ = inject(UniversalSearchService);

  @ViewChild(MatAutocompleteTrigger) chipAuto: MatAutocompleteTrigger;

  @Input() field;
  @Input() filterForm;
  @Input() groupKey;
  @Output() selectionChange = new EventEmitter();
  @Output() clearFilter = new EventEmitter();
  unsubscribe = new Subject();
  dropdownLoading: boolean;
  globals = this.ss.get('globals');
  managedUnits = 0;
  dropdownNoResults: boolean;

  userDropdownLoading = false;
  userDropdownNoResults = false;
  userControl = new FormControl();
  filteredUsers = [];
  previousSelectedUser;
  fetchingNpns = false;

  chips = signal([]);
  separatorKeysCodes: number[] = [ENTER, COMMA];
  currentChip = model('');
  chipOptions = signal([]);
  filteredChips = computed(() => {
    const currentChip = this.currentChip().toLowerCase();
    return currentChip
      ? this.chipOptions().filter(
          chip =>
            chip.Name.toLowerCase().includes(currentChip) ||
            chip.NPN.toLowerCase().includes(currentChip)
        )
      : this.chipOptions().slice();
  });

  ngOnInit(): void {
    this.managedUnits = this.globals.user.managerUnitCount;
    this.userControl.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(value => {
        this.dropdownLoading = true;
        if (value && typeof value === 'string') {
          return this._filterReps(value);
        }
      });

    if (this.managedUnits === 0) {
      this.fetchingNpns = true;
      this.dtccSvc.getNPNs(this.globals.user.id).subscribe(resp => {
        this.fetchingNpns = false;
        this.chipOptions.set(resp.npns);
      });
    }

    this.cstmFltrSrvc.updateDropdowns$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: field => {
          if (field) {
            if (
              field['field'] === this.field.varName &&
              (typeof field['value'] === 'number' || field['value'])
            ) {
              if (Array.isArray(field['value'])) {
                this.chips.set(field['value']);
              } else {
                this.chips.set([field['value']]);
                this.chips.set([field['values']['value']]);
              }
            } else if (
              field['varName'] === this.field.varName &&
              field['values']?.['value']
            ) {
              if (Array.isArray(field['values']['value'])) {
                this.chips.set(field['values']['value']);
              } else {
                this.chips.set([field['values']['value']]);
              }
            } else if (
              field['varName'] === this.field.varName &&
              !field['values']?.['value']
            ) {
              this.userControl.setValue('');
              this.chips.set([]);
            }

            if (
              this.field.conditionalField &&
              field['varName'] === this.field.conditionalField &&
              field?.['values']?.['value'] &&
              (field['values']['value'] === this.field.conditionalValue ||
                field['values']['value'].includes(this.field.conditionalValue))
            ) {
              this.userControl.enable();
            }
          }
        },
        error: err => {
          console.error('Error: ', err);
        },
      });

    if (this.field.conditionalField) {
      this.cstmFltrSrvc.updateConditionalFilter$
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: passedFilter => {
            if (
              passedFilter['varName'] === this.field.conditionalField &&
              (passedFilter['values'] === this.field.conditionalValue ||
                passedFilter['values'].includes(this.field.conditionalValue))
            ) {
              this.userControl.enable();
            } else if (
              passedFilter['varName'] === this.field.conditionalField &&
              (passedFilter['values'] !== this.field.conditionalValue ||
                !passedFilter['values'].includes(this.field.conditionalValue))
            ) {
              this.chips.update(() => []);
              this.currentChip.set('');
              this.selectionChange.emit({
                option: {
                  value: [],
                },
              });
              this.userControl.disable();
            }
          },
          error: error => console.error('Error: ', error),
        });
    }

    if (this.field.options) {
      this.chipOptions.set(this.field.options);
    }

    this.cstmFltrSrvc.reloadFilters$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: () => {
          if (this.field.controlType === 'chip') {
            this.chipOptions.set(this.field.options);
          }
        },
        error: error => alert(error),
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();

    if (value) {
      this.chips.update(chips => [...chips, value]);
    }

    // Clear the input value
    this.currentChip.set('');
  }

  remove(fruit: string): void {
    this.chips.update(chips => {
      const index = chips.indexOf(fruit);
      if (index < 0) {
        return chips;
      }

      chips.splice(index, 1);
      return [...chips];
    });

    this.selectionChange.emit({
      option: {
        value: this.chips(),
      },
    });
  }

  private _filterReps(value: string) {
    this.dropdownLoading = true;
    this.usServ.userSearch(value).subscribe(res => {
      if (res && res.names.length > 0) {
        this.dropdownNoResults = false;
        this.filteredUsers = res.names;
      } else if (!res || res.names.length === 0) {
        this.dropdownNoResults = true;
      }
      this.dropdownLoading = false;
    });
  }

  displayUser(val) {
    return val ? `${val.name}` : '';
  }

  changeSelection(ev) {
    this.fetchingNpns = true;
    this.dtccSvc.getNPNs(ev.option.value.id).subscribe(resp => {
      this.fetchingNpns = false;
      this.chipOptions.set(resp.npns);
    });
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    if (!this.chips().includes(event.option.value)) {
      this.chips.update(chips => [...chips, event.option.value]);
      this.selectionChange.emit({
        option: {
          value: this.chips(),
        },
      });
    }
    this.currentChip.set('');
    event.option.deselect();
  }

  onBlur(ev) {
    if (ev.relatedTarget) {
      this.chipAuto.closePanel();
    }
  }

  chipDisplay(val) {
    const dispChip = this.chipOptions().find(opt => opt.NPN === val);
    if (dispChip) {
      return `${dispChip.Name} - ${dispChip.NPN}`;
    }

    return val;
  }
}
